'use strict';

const {
	getCookieConsentInstance,
	honorGlobalPrivacyControlIfEnabled,
	isGlobalPrivacyControlEnabled,
	isPrivacySaleShareOptedOut,
	persistPrivacySaleShareOptOut,
} = require('../consent/privacy-rights');
const {
	PRIVACY_REQUEST_COPY,
	resolvePrivacyRequestLanguage,
} = require('./copy');

function formatOptOutDate(value) {
	if (typeof value !== 'string' || value === '') {
		return '';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	return date.toLocaleDateString(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});
}

function resolveBannerState(cookieConsent) {
	if (isGlobalPrivacyControlEnabled()) {
		return 'gpc';
	}

	if (isPrivacySaleShareOptedOut(cookieConsent)) {
		return 'optedOut';
	}

	return 'notOptedOut';
}

const BANNER_ICON_URLS = {
	gpc: 'https://cdn-static.revolutionparts.io/assets/images/storefront/privacy/privacy-shield-lock.svg',
	notOptedOut: 'https://cdn-static.revolutionparts.io/assets/images/storefront/privacy/privacy-shield-slash.svg',
	optedOut: 'https://cdn-static.revolutionparts.io/assets/images/storefront/privacy/privacy-shield-check.svg',
};

function createBannerIcon(state) {
	const icon = document.createElement('span');
	icon.className = 'privacy-request-banner__icon privacy-request-banner__icon--' + state;
	icon.setAttribute('aria-hidden', 'true');

	const img = document.createElement('img');
	img.src = BANNER_ICON_URLS[state] || BANNER_ICON_URLS.notOptedOut;
	img.alt = '';
	img.width = 48;
	img.height = 48;
	icon.appendChild(img);

	return icon;
}

function createTextColumn(titleText, bodyText) {
	const column = document.createElement('div');
	column.className = 'privacy-request-banner__text';

	const title = document.createElement('p');
	title.className = 'privacy-request-banner__title';
	title.textContent = titleText;

	const body = document.createElement('p');
	body.className = 'privacy-request-banner__body';
	body.textContent = bodyText;

	column.appendChild(title);
	column.appendChild(body);
	return column;
}

function createOptOutButton(label, onClick) {
	const button = document.createElement('button');
	button.type = 'button';
	button.className = 'privacy-request-banner__opt-out';
	button.textContent = label;
	button.addEventListener('click', onClick);
	return button;
}

function createManageCookiesLink(label) {
	const link = document.createElement('a');
	link.className = 'privacy-request-banner__manage-cookies';
	link.href = '#';
	link.textContent = label;
	link.addEventListener('click', function (event) {
		event.preventDefault();
		if (typeof globalThis.showCookiePreferences === 'function') {
			globalThis.showCookiePreferences();
			return;
		}

		document.dispatchEvent(new CustomEvent('rp:open-cookie-preferences'));
	});
	return link;
}

function renderStatusBanner(card, language, onOptOut) {
	const copy = PRIVACY_REQUEST_COPY[language];
	const cookieConsent = getCookieConsentInstance();
	const state = resolveBannerState(cookieConsent);
	const banner = document.createElement('div');
	banner.className = 'privacy-request-banner privacy-request-banner--' + state;
	banner.setAttribute('role', 'status');
	banner.setAttribute('aria-live', 'polite');

	banner.appendChild(createBannerIcon(state));

	if (state === 'gpc') {
		banner.appendChild(createTextColumn(copy.bannerGpcTitle, copy.bannerGpcBody));
		banner.appendChild(createManageCookiesLink(copy.manageCookieSettings));
	} else if (state === 'optedOut') {
		banner.appendChild(createTextColumn(copy.bannerOptedOutTitle, copy.bannerOptedOutBody));
		const actions = document.createElement('div');
		actions.className = 'privacy-request-banner__actions';
		const cookieData = cookieConsent && typeof cookieConsent.getCookie === 'function'
			? cookieConsent.getCookie('data')
			: null;
		const optedAt = cookieData && typeof cookieData.saleShareOptedOutAt === 'string'
			? cookieData.saleShareOptedOutAt
			: null;
		const dateLabel = formatOptOutDate(optedAt);
		if (dateLabel !== '') {
			const badge = document.createElement('span');
			badge.className = 'privacy-request-banner__date';
			badge.textContent = dateLabel;
			actions.appendChild(badge);
		}
		actions.appendChild(createManageCookiesLink(copy.manageCookieSettings));
		banner.appendChild(actions);
	} else {
		banner.appendChild(createTextColumn(copy.bannerNotOptedOutTitle, copy.bannerNotOptedOutBody));
		banner.appendChild(createOptOutButton(copy.optOutButton, onOptOut));
	}

	card.replaceChildren(banner);
}

function initPrivacyRequestConsentControls(root) {
	if (root.dataset.privacyRequestConsentInitialized === 'true') {
		return;
	}

	root.dataset.privacyRequestConsentInitialized = 'true';
	const languageSelector = document.querySelector('select.language-selector');
	const language = resolvePrivacyRequestLanguage(document.documentElement.lang) === 'fr'
		|| (
			languageSelector instanceof HTMLSelectElement
			&& resolvePrivacyRequestLanguage(languageSelector.value) === 'fr'
		)
		? 'fr'
		: 'en';
	const copy = PRIVACY_REQUEST_COPY[language];
	const statusNode = root.querySelector('[data-privacy-request-status]');
	const statusCard = root.querySelector('.privacy-request-page__status-card')
		|| (statusNode instanceof HTMLElement ? statusNode.parentElement : null);
	const saleShareContainer = root.querySelector('[data-privacy-request-control="sale-share-opt-out"]');
	const sensitivePiContainer = root.querySelector('[data-privacy-request-control="sensitive-pi-limit"]');
	const gpcDescription = root.querySelector('[data-privacy-request-gpc-description]');

	if (gpcDescription instanceof HTMLElement) {
		gpcDescription.textContent = copy.gpcDescription;
	}

	if (saleShareContainer instanceof HTMLElement) {
		saleShareContainer.replaceChildren();
		saleShareContainer.hidden = true;
	}

	if (sensitivePiContainer instanceof HTMLElement) {
		sensitivePiContainer.replaceChildren();
		sensitivePiContainer.hidden = true;
	}

	const cookieConsent = getCookieConsentInstance();
	if (cookieConsent) {
		honorGlobalPrivacyControlIfEnabled(cookieConsent);
	}

	if (!(statusCard instanceof HTMLElement)) {
		return;
	}

	const refreshBanner = function () {
		renderStatusBanner(statusCard, language, function () {
			persistPrivacySaleShareOptOut(getCookieConsentInstance(), true);
			refreshBanner();
		});
	};

	refreshBanner();
}

module.exports = {
	initPrivacyRequestConsentControls,
};
